import { ICardResponse } from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import {
  BulkStatus,
  IAdditionalData,
  IBankStatement,
  IBulkCollaboratorBatch,
  IBulkCollaboratorItem,
  IBulkPayableBatch,
  IBulkPayableItem,
  IClientInvoice,
  ICollaboratorEvents,
  ICollaboratorGroupResponse,
  ICollaboratorSchema,
  IInstantPayoutResponse,
  IInvoice,
  IInvoiceFeeCalculation,
  IInvoiceTemplate,
  IMemberClient,
  InvoiceStatus,
  IPayrollSettings,
  IServiceStatusResponse,
  MemberClientTaxStatus
} from "@wingspanhq/payments/dist/interfaces";
import { IDeductionResponse } from "@wingspanhq/payments/dist/interfaces/api/deductions";
import {
  DeductionStatus,
  DeductionType
} from "@wingspanhq/payments/dist/interfaces/deductions";
import { IEligibilityRequirement } from "@wingspanhq/payments/dist/interfaces/eligibilityRequirement";
import { AxiosError } from "axios";
import times from "lodash/times";
import flatten from "lodash/flatten";
import { InfiniteQueryConfig, QueryConfig } from "react-query";
import {
  CollaboratorFilter,
  ICardDetailsResponse,
  paymentsService
} from "../../services/payments";
import { useAuthorizedScopeGroups } from "../../Settings/utils/teamUtils";
import { Await } from "../../utils";
import { concurrentActions, getAllEntries } from "../../utils/serviceHelper";
import { useWSInfiniteQuery, useWSQuery } from "../helpers";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import {
  QUERY_ALL_COLLABORATOR_GROUPS,
  QUERY_BANK_INSTITUTION,
  QUERY_BANK_STATEMENT,
  QUERY_BANK_STATEMENTS_LIST,
  QUERY_BULK_COLLABORATOR_BATCH_ITEM_LIST,
  QUERY_BULK_COLLABORATOR_BATCH_ITEM_NOT_STARTED_LIST,
  QUERY_BULK_PAYABLE_BATCH,
  QUERY_BULK_PAYABLE_BATCH_ITEM,
  QUERY_BULK_PAYABLE_BATCH_ITEM_LIST,
  QUERY_BULK_PAYABLE_BATCH_ITEM_NOT_STARTED_LIST,
  QUERY_CARD,
  QUERY_CARD_LIST,
  QUERY_CLIENT_DEDUCTION,
  QUERY_CLIENT_DEDUCTIONS,
  QUERY_CLIENT_INVOICE,
  QUERY_CLIENT_INVOICE_FEES,
  QUERY_COLLABORATOR,
  QUERY_COLLABORATORS,
  QUERY_COLLABORATOR_DEDUCTION,
  QUERY_COLLABORATOR_DEDUCTIONS,
  QUERY_COLLABORATOR_EVENTS,
  QUERY_COLLABORATOR_GROUP,
  QUERY_COLLABORATOR_GROUPS,
  QUERY_COLLABORATOR_SETTINGS_ADDITIONAL_DATA,
  QUERY_COLLABORATOR_SETTINGS_ADDITIONAL_ITEM_DATA,
  QUERY_ELIGIBILITY_REQUIREMENT,
  QUERY_ELIGIBILITY_REQUIREMENTS,
  QUERY_INSTANT_PAYOUT,
  QUERY_INVOICE,
  QUERY_INVOICES,
  QUERY_INVOICE_TEMPLATE,
  QUERY_INVOICE_TEMPLATES,
  QUERY_MEMBER_CLIENT,
  QUERY_MEMBER_CLIENTS,
  QUERY_MISSING_INFO_COLLABORATORS,
  QUERY_PAYMENTS_STATUS,
  QUERY_PAYOUT_SETTINGS,
  QUERY_PAYOUT_SETTINGS_DEBIT_CARDS,
  QUERY_PAYROLL_SETTINGS
} from "./keys";
import { IBulkCalculation1099Batch } from "@wingspanhq/payments/dist/interfaces/bulkCalculation1099";
import { TaxFormStatus } from "@wingspanhq/payments/dist/interfaces/taxForm";

export const usePaymentsStatusQuery = (
  queryConfig?: QueryConfig<IServiceStatusResponse, AxiosError>
) => {
  const { hasPaymentsScope } = useAuthorizedScopeGroups();

  return useWSQuery(QUERY_PAYMENTS_STATUS, paymentsService.service.get, {
    ...queryConfig,
    enabled: hasPaymentsScope
  });
};

export enum InvoicesStatusFilter {
  Unsent = "Unsent",
  Open = "Open",
  Paid = "Paid"
}

export enum InvoicesDateRangeFilter {
  All = "All",
  PastWeek = "PastWeek",
  PastMonth = "PastMonth",
  PastYear = "PastYear",
  Custom = "Custom"
}
export interface PayablesFilters {
  status: InvoicesStatusFilter[];
  memberClientId: string;
  dueDateRange: InvoicesDateRangeFilter;
  customDueDateRange?: Date[];
}

export const useInvoicesQuery = (
  config?: QueryConfig<IInvoice[], AxiosError>
) => {
  const { hasPaymentsScope } = useAuthorizedScopeGroups();

  return useWSQuery<IInvoice[], AxiosError>(
    QUERY_INVOICES,
    () =>
      getAllEntries(paymentsService.invoice.list, {
        status: {
          in: [
            InvoiceStatus.Draft,
            InvoiceStatus.Open,
            InvoiceStatus.Overdue,
            InvoiceStatus.Paid,
            InvoiceStatus.PaymentInTransit,
            InvoiceStatus.Pending
          ]
        }
      }),
    {
      refetchOnMount: false,
      retry: false,
      enabled: hasPaymentsScope,
      ...config
    }
  );
};

export const useInvoiceQuery = (
  invoiceId: string,
  config?: QueryConfig<IInvoice, AxiosError>
) => {
  return useWSQuery<IInvoice, AxiosError>(
    [QUERY_INVOICE, invoiceId],
    () => paymentsService.invoice.get(invoiceId),
    {
      ...config
    }
  );
};

export const useInvoiceTemplatesQuery = (
  config?: QueryConfig<IInvoiceTemplate[], AxiosError>
) => {
  const { hasPaymentsScope } = useAuthorizedScopeGroups();

  return useWSQuery<IInvoiceTemplate[], AxiosError>(
    QUERY_INVOICE_TEMPLATES,
    () => getAllEntries(paymentsService.invoiceTemplate.list),
    {
      refetchOnMount: false,
      retry: false,
      enabled: hasPaymentsScope,
      ...config
    }
  );
};

export const useInvoiceTemplateQuery = (
  invoiceTemplateId: string,
  config?: QueryConfig<IInvoiceTemplate, AxiosError>
) => {
  return useWSQuery<IInvoiceTemplate, AxiosError>(
    [QUERY_INVOICE_TEMPLATE, invoiceTemplateId],
    () => paymentsService.invoiceTemplate.get(invoiceTemplateId),
    {
      ...config
    }
  );
};

export const useMemberClientsQuery = (
  config?: QueryConfig<IMemberClient[], AxiosError>
) => {
  return useWSQuery<IMemberClient[], AxiosError>(
    QUERY_MEMBER_CLIENTS,
    () =>
      getAllEntries(paymentsService.memberClient.list, undefined, {
        updatedAt: "desc"
      }),
    {
      refetchOnMount: false,
      retry: false,
      ...config
    }
  );
};

export const useMemberClientQuery = (
  memberClientId: string,
  config?: QueryConfig<IMemberClient, AxiosError>
) => {
  return useWSQuery<IMemberClient, AxiosError>(
    [QUERY_MEMBER_CLIENT, memberClientId],
    () => paymentsService.memberClient.get(memberClientId),
    config
  );
};

export interface collaboratorsFilters {
  taxStatus: MemberClientTaxStatus;
}
export const useMissingInfoCollaboratorsQuery = (
  config?: QueryConfig<ICollaboratorSchema[], AxiosError>
) => {
  const _filters: CollaboratorFilter = {
    taxStatus: {
      "=": MemberClientTaxStatus.Incomplete
    }
  };
  return useWSQuery<ICollaboratorSchema[], AxiosError>(
    [QUERY_MISSING_INFO_COLLABORATORS, { _filters }],
    async () => {
      const listSize = await paymentsService.collaborator.listSize();
      const pages = Math.ceil(listSize / 100);

      const actions = times(pages).map((_, i) => () =>
        paymentsService.collaborator.list({
          filter: _filters,
          page: {
            size: 100,
            number: i + 1
          }
        })
      );

      const allPages = await concurrentActions(actions, {
        concurrentLimit: 5
      });

      return flatten(allPages);
    },
    {
      refetchOnMount: false,
      retry: false,
      ...config
    }
  );
};

export const useCollaboratorsQuery = (
  filters?: collaboratorsFilters,
  config?: QueryConfig<ICollaboratorSchema[], AxiosError>
) => {
  return useWSQuery<ICollaboratorSchema[], AxiosError>(
    [QUERY_COLLABORATORS, { filters }],
    async () => {
      const listSize = await paymentsService.collaborator.listSize();
      const pages = Math.ceil(listSize / 100);

      const actions = times(pages).map((_, i) => () =>
        paymentsService.collaborator.list({
          page: { size: 100, number: i + 1 }
        })
      );

      const allPages = await concurrentActions(actions, {
        concurrentLimit: 5
      });

      return flatten(allPages);
    },
    {
      refetchOnMount: false,
      retry: false,
      ...config
    }
  );
};

export const useCollaboratorQuery = (
  collaboratorId: string,
  config?: QueryConfig<ICollaboratorSchema, AxiosError>
) => {
  return useWSQuery<ICollaboratorSchema, AxiosError>(
    [QUERY_COLLABORATOR, collaboratorId],
    () => paymentsService.collaborator.get(collaboratorId),
    config
  );
};

export const useCollaboratorEventsQuery = (
  collaboratorId: string,
  config?: QueryConfig<ICollaboratorEvents, AxiosError>
) => {
  return useWSQuery<ICollaboratorEvents, AxiosError>(
    [QUERY_COLLABORATOR_EVENTS, collaboratorId],
    () => paymentsService.collaborator.getEvents(collaboratorId),
    config
  );
};

export const useClientInvoiceQuery = (
  id: string,
  config?: QueryConfig<IClientInvoice, AxiosError>
) => {
  return useWSQuery<IClientInvoice, AxiosError>(
    [QUERY_CLIENT_INVOICE, id],
    () => paymentsService.client.invoice.get(id),
    {
      refetchOnMount: false,
      retry: false,
      ...config
    }
  );
};

export const useClientInvoiceFeesQuery = (
  id: string,
  config?: QueryConfig<IInvoiceFeeCalculation, AxiosError>
) => {
  return useWSQuery<IInvoiceFeeCalculation, AxiosError>(
    [QUERY_CLIENT_INVOICE_FEES, id],
    () => paymentsService.client.invoice.getFees(id),
    {
      refetchOnMount: false,
      retry: false,
      ...config
    }
  );
};

export const useBankStatementsList = (
  config?: QueryConfig<IBankStatement[], AxiosError>
) => {
  return useWSQuery<IBankStatement[], AxiosError>(
    QUERY_BANK_STATEMENTS_LIST,
    paymentsService.banking.statement.list,
    {
      refetchOnMount: false,
      ...config
    }
  );
};

export const useGetBankStatement = (
  statementId: string,
  config?: QueryConfig<any, AxiosError>
) => {
  return useWSQuery<any, AxiosError>(
    QUERY_BANK_STATEMENT,
    () => paymentsService.banking.statement.get(statementId),
    {
      refetchOnMount: false,
      ...config
    }
  );
};

export const useListCollaboratorSettingsAdditionalData = (
  config?: QueryConfig<IAdditionalData[], AxiosError>
) => {
  return useWSQuery<IAdditionalData[], AxiosError>(
    QUERY_COLLABORATOR_SETTINGS_ADDITIONAL_DATA,
    paymentsService.collaboratorSettings.list,
    {
      refetchOnMount: false,
      ...config
    }
  );
};

export const useCollaboratorSettingsAdditionalData = (
  id: string,
  config?: QueryConfig<IAdditionalData & { id: string }, AxiosError>
) => {
  return useWSQuery<IAdditionalData & { id: string }, AxiosError>(
    [QUERY_COLLABORATOR_SETTINGS_ADDITIONAL_ITEM_DATA, id],
    async () => await paymentsService.collaboratorSettings.item(id),
    {
      refetchOnMount: false,
      ...config
    }
  );
};

export const usePayrollSettings = (
  memberId: string,
  config?: QueryConfig<IPayrollSettings, AxiosError>
) => {
  return useWSQuery<IPayrollSettings, AxiosError>(
    QUERY_PAYROLL_SETTINGS,
    () => paymentsService.payrollSettings.get(memberId),
    config
  );
};

export const useInstantPayoutQuery = (
  config?: QueryConfig<IInstantPayoutResponse, AxiosError>
) => {
  return useWSQuery<IInstantPayoutResponse, AxiosError>(
    QUERY_INSTANT_PAYOUT,
    paymentsService.banking.instantPayout.list,
    config
  );
};

export const useCardsQuery = (
  config?: QueryConfig<ICardResponse[], AxiosError>
) => {
  return useWSQuery<ICardResponse[], AxiosError>(
    QUERY_CARD_LIST,
    paymentsService.banking.card.list,
    {
      retry: false,
      refetchOnMount: false,
      ...config
    }
  );
};

export const useCardQuery = (
  id: string,
  config?: QueryConfig<ICardDetailsResponse, AxiosError>
) => {
  return useWSQuery<ICardDetailsResponse, AxiosError>(
    [QUERY_CARD, id],
    () => paymentsService.banking.card.get(id),
    {
      refetchOnMount: false,
      ...config
    }
  );
};

export const useCollaboratorGroupsQuery = (
  config?: QueryConfig<ICollaboratorGroupResponse[], AxiosError>
) => {
  return useWSQuery<ICollaboratorGroupResponse[], AxiosError>(
    QUERY_COLLABORATOR_GROUPS,
    () => getAllEntries(paymentsService.collaboratorGroup.list),
    {
      refetchOnMount: false,
      retry: false,
      ...config
    }
  );
};

export const useCollaboratorGroupQuery = (
  collaboratorGroupId: string,
  config?: QueryConfig<ICollaboratorGroupResponse, AxiosError>
) => {
  return useWSQuery<ICollaboratorGroupResponse, AxiosError>(
    [QUERY_COLLABORATOR_GROUP, collaboratorGroupId],
    () => paymentsService.collaboratorGroup.get(collaboratorGroupId),
    config
  );
};

export const useAllCollaboratorGroupsQuery = (
  sort: { [key: string]: "asc" | "desc" | undefined },
  queryConfig?: InfiniteQueryConfig<ICollaboratorGroupResponse[], AxiosError>
) =>
  useWSInfiniteQuery(
    [QUERY_ALL_COLLABORATOR_GROUPS, sort],
    async (query, queryParams, pageNumber = 1) => {
      return await paymentsService.collaboratorGroup.list({
        page: {
          size: 10,
          number: pageNumber
        },
        sort
      });
    },
    {
      getFetchMore: (lastPage, allPages) => {
        if (lastPage.length < 10) {
          return undefined;
        }

        return allPages.length + 1;
      },
      ...queryConfig
    }
  );

export const useEligibilityRequirementQuery = (
  id: string,
  config?: QueryConfig<IEligibilityRequirement, AxiosError>
) => {
  return useWSQuery<IEligibilityRequirement, AxiosError>(
    [QUERY_ELIGIBILITY_REQUIREMENT, id],
    () => paymentsService.collaboratorSettings.eligibilityRequirements.get(id),
    config
  );
};

export const useEligibilityRequirementsQuery = (
  config?: QueryConfig<IEligibilityRequirement[], AxiosError>
) => {
  return useWSQuery<IEligibilityRequirement[], AxiosError>(
    QUERY_ELIGIBILITY_REQUIREMENTS,
    () =>
      getAllEntries(
        paymentsService.collaboratorSettings.eligibilityRequirements.list
      ),
    config
  );
};

export const usePayoutSettings = (
  memberId: string,
  config?: QueryConfig<
    Await<ReturnType<typeof paymentsService.payoutSettings.get>>,
    AxiosError
  >
) => {
  return useWSQuery<
    Await<ReturnType<typeof paymentsService.payoutSettings.get>>,
    AxiosError
  >(
    [QUERY_PAYOUT_SETTINGS, memberId],
    () => paymentsService.payoutSettings.get(memberId),
    {
      refetchOnMount: false,
      ...config
    }
  );
};

export const usePayoutSettingsDebitCards = (
  memberId: string,
  config?: QueryConfig<
    Await<ReturnType<typeof paymentsService.payoutSettings.debitCard.list>>,
    AxiosError
  >
) => {
  return useWSQuery<
    Await<ReturnType<typeof paymentsService.payoutSettings.debitCard.list>>,
    AxiosError
  >(
    [QUERY_PAYOUT_SETTINGS_DEBIT_CARDS, memberId],
    () => paymentsService.payoutSettings.debitCard.list(memberId),
    {
      refetchOnMount: false,
      ...config
    }
  );
};

export const usePayoutSettingsDebitCard = (
  memberId: string,
  debitCardId: string,
  config?: QueryConfig<
    Await<ReturnType<typeof paymentsService.payoutSettings.debitCard.get>>,
    AxiosError
  >
) => {
  return useWSQuery<
    Await<ReturnType<typeof paymentsService.payoutSettings.debitCard.get>>,
    AxiosError
  >(
    [`QUERY_PAYOUT_SETTINGS_DEBIT_CARD-${debitCardId}`, memberId],
    () => paymentsService.payoutSettings.debitCard.get(memberId, debitCardId),
    {
      refetchOnMount: false,
      ...config
    }
  );
};

export const useBankInstitution = (
  institutionId: string,
  config?: QueryConfig<
    Await<ReturnType<typeof paymentsService.banking.institution.get>>,
    AxiosError
  >
) => {
  return useWSQuery<
    Await<ReturnType<typeof paymentsService.banking.institution.get>>,
    AxiosError
  >(
    [QUERY_BANK_INSTITUTION, institutionId],
    () => paymentsService.banking.institution.get(institutionId),
    config
  );
};

export const useCollaboratorDeduction = (
  id: string,
  config?: QueryConfig<IDeductionResponse, AxiosError>
) => {
  return useWSQuery<IDeductionResponse, AxiosError>(
    [QUERY_COLLABORATOR_DEDUCTION, id],
    () => paymentsService.collaboratorDeductions.get(id),
    config
  );
};

export const useCollaboratorDeductions = (
  params: {
    memberId?: string;
    clientId?: string;
    status?: DeductionStatus | { in: DeductionStatus[] };
    type?: DeductionType | { in: DeductionType[] };
  },
  config?: QueryConfig<IDeductionResponse[], AxiosError>
) => {
  const featureFlagsQuery = useFeatureFlags();

  return useWSQuery<IDeductionResponse[], AxiosError>(
    [QUERY_COLLABORATOR_DEDUCTIONS, params],
    () =>
      featureFlagsQuery.data?.deductions
        ? paymentsService.collaboratorDeductions.list({
            filter: params,
            page: { size: 100 }
          })
        : [],
    config
  );
};

export const useClientDeduction = (
  id: string,
  config?: QueryConfig<IDeductionResponse, AxiosError>
) => {
  return useWSQuery<IDeductionResponse, AxiosError>(
    [QUERY_CLIENT_DEDUCTION, id],
    () => paymentsService.clientDeductions.get(id),
    config
  );
};

export const useClientDeductions = (
  params: {
    memberId?: string;
    clientId?: string;
    status?: DeductionStatus;
    type?: DeductionType;
  },
  config?: QueryConfig<IDeductionResponse[], AxiosError>
) => {
  const featureFlagsQuery = useFeatureFlags();

  return useWSQuery<IDeductionResponse[], AxiosError>(
    [QUERY_CLIENT_DEDUCTIONS, params],
    () =>
      featureFlagsQuery.data?.deductions
        ? paymentsService.clientDeductions.list({
            filter: params,
            page: { size: 100 }
          })
        : [],
    config
  );
};

export const useBulkPayableBatch = (
  batchId: string,
  config?: QueryConfig<IBulkPayableBatch, AxiosError>
) => {
  return useWSQuery<IBulkPayableBatch, AxiosError>(
    [QUERY_BULK_PAYABLE_BATCH, batchId],
    () => paymentsService.bulkPayable.batch.get(batchId),
    config
  );
};

export const useBulkPayableBatchItem = (
  batchId: string,
  batchItemId: string,
  config?: QueryConfig<IBulkPayableItem, AxiosError>
) => {
  return useWSQuery<IBulkPayableItem, AxiosError>(
    [QUERY_BULK_PAYABLE_BATCH_ITEM, batchId],
    () => paymentsService.bulkPayable.batchItem.get(batchId, batchItemId),
    config
  );
};

export const useBulkPayableBatchItems = (
  batchId: string,
  status?: BulkStatus[],
  config?: QueryConfig<
    Await<ReturnType<typeof paymentsService.bulkPayable.batchItem.list>>,
    AxiosError
  >
) => {
  return useWSQuery<
    Await<ReturnType<typeof paymentsService.bulkPayable.batchItem.list>>,
    AxiosError
  >(
    QUERY_BULK_PAYABLE_BATCH_ITEM_LIST,
    () =>
      paymentsService.bulkPayable.batchItem.list(
        batchId,
        status?.length
          ? status
          : [
              BulkStatus.Open,
              BulkStatus.Pending,
              BulkStatus.Processing,
              BulkStatus.Complete,
              BulkStatus.Failed
            ]
      ),
    config
  );
};

export const useBulkPayableBatchItemsNotYetProcessed = (
  batchId: string,
  config?: QueryConfig<
    Await<ReturnType<typeof paymentsService.bulkPayable.batchItem.list>>,
    AxiosError
  >
) => {
  return useWSQuery<
    Await<ReturnType<typeof paymentsService.bulkPayable.batchItem.list>>,
    AxiosError
  >(
    QUERY_BULK_PAYABLE_BATCH_ITEM_NOT_STARTED_LIST,
    () =>
      paymentsService.bulkPayable.batchItem.list(batchId, [
        BulkStatus.Open,
        BulkStatus.Pending,
        BulkStatus.Processing
      ]),
    config
  );
};

export const useBulkCollaboratorBatch = (
  batchId: string,
  config?: QueryConfig<IBulkCollaboratorBatch, AxiosError>
) => {
  return useWSQuery<IBulkCollaboratorBatch, AxiosError>(
    [QUERY_BULK_PAYABLE_BATCH, batchId],
    () => paymentsService.bulkCollaborator.batch.get(batchId),
    config
  );
};

export const useBulkCollaboratorBatchItem = (
  batchId: string,
  batchItemId: string,
  config?: QueryConfig<IBulkCollaboratorItem, AxiosError>
) => {
  return useWSQuery<IBulkCollaboratorItem, AxiosError>(
    [QUERY_BULK_PAYABLE_BATCH_ITEM, batchId],
    () => paymentsService.bulkCollaborator.batchItem.get(batchId, batchItemId),
    config
  );
};

export const useBulkCollaboratorBatchItems = (
  batchId: string,
  status?: BulkStatus[],
  config?: QueryConfig<
    Await<ReturnType<typeof paymentsService.bulkCollaborator.batchItem.list>>,
    AxiosError
  >
) => {
  return useWSQuery<
    Await<ReturnType<typeof paymentsService.bulkCollaborator.batchItem.list>>,
    AxiosError
  >(
    QUERY_BULK_COLLABORATOR_BATCH_ITEM_LIST,
    () =>
      paymentsService.bulkCollaborator.batchItem.list(
        batchId,
        status?.length
          ? status
          : [
              BulkStatus.Open,
              BulkStatus.Pending,
              BulkStatus.Processing,
              BulkStatus.Complete,
              BulkStatus.Failed
            ]
      ),
    config
  );
};

export const useBulkCollaboratorBatchItemsNotYetProcessed = (
  batchId: string,
  config?: QueryConfig<
    Await<ReturnType<typeof paymentsService.bulkCollaborator.batchItem.list>>,
    AxiosError
  >
) => {
  return useWSQuery<
    Await<ReturnType<typeof paymentsService.bulkCollaborator.batchItem.list>>,
    AxiosError
  >(
    QUERY_BULK_COLLABORATOR_BATCH_ITEM_NOT_STARTED_LIST,
    () =>
      paymentsService.bulkCollaborator.batchItem.list(batchId, [
        BulkStatus.Open,
        BulkStatus.Pending,
        BulkStatus.Processing
      ]),
    config
  );
};

export const useBulkCalculate1099Batch = (
  batchId: string,
  config?: QueryConfig<IBulkCalculation1099Batch, AxiosError>
) => {
  return useWSQuery<IBulkCalculation1099Batch, AxiosError>(
    [QUERY_BULK_PAYABLE_BATCH, batchId],
    () =>
      paymentsService.collaborator.nec1099.bulkCalculation1099Batch.get(
        batchId
      ),
    config
  );
};
